/**
 * Wave DOC (decision 0068, spec `docs/design/documents-2026-10-12/spec.md` §2, §4): documents in a project.
 *
 * A document is an artifact with `doc: {path, url}` set: reference material a person (or Claude Code acting for them)
 * added, always project-level (`itemId: null`). Kinds people see come from {@link docKindOf}. JS, CSS and fonts are
 * support files: hidden in lists, they only feed HTML views.
 *
 * No I/O. This module must not import `./mcp` or `./domain/*` at runtime: `domain/knowledge.ts` and
 * `commands/index.ts` import {@link DocMeta} from here (only a type comes from `./mcp`).
 */
import { Schema, SchemaGetter } from "effect"
import { Actor, tolerantLiterals } from "./common"
import { DOC_PATH_MAX, isDocPath } from "./doc-path"
import {
  contentTypeOfExt,
  FILE_MAX_BYTES,
  type FileContentType,
  fileContentTypeOf,
  shaOfFileUri,
} from "./files"
import { AgentId, ArtifactId, Millis, NonNegInt, PosInt, Sha256, ShortRef } from "./ids"
import type { PersonToolMeta } from "./mcp"

export * from "./doc-bundle"
export * from "./doc-path"

// #region paths and links
/** A document's place in the project (`docs/design/x/README.md`); the rules are in `doc-path.ts`. */
export const DocPath = Schema.String.check(
  Schema.makeFilter((s: string) =>
    isDocPath(s)
      ? true
      : "a document path: NFC, '/' separated, no leading '/', no '.' or '..' or empty segment, no '\\', '?', '#', control or format characters, ≤ 300 characters (≤ 120 per segment)",
  ),
)

export const DOC_URL_MAX = 2_000

const BLOCKED_SUFFIXES = [".local", ".internal", ".lan", ".localhost", ".test"]
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
// the last label of a public name starts with a letter (an all-numeric or 0x… label makes the host an IPv4 address)
const TLD = /^(?:[a-z][a-z0-9-]{0,61}[a-z0-9]|[a-z])$/
// scheme, host (no userinfo, no port, no brackets), then nothing or a path, query or fragment
const HTTPS_URL = /^https:\/\/([A-Za-z0-9.-]+)([/?#][^\s\\]*)?$/

/** The lowercased host of a URL {@link isDocUrl} accepts, else null. */
export const docUrlHost = (url: string): string | null => {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters and spaces are what this rejects
  if (url.length > DOC_URL_MAX || /[\u0000- \u007f-\u009f]/.test(url)) return null
  const m = HTTPS_URL.exec(url)
  const host = m?.[1]?.toLowerCase()
  if (host === undefined || host.length > 253) return null
  const labels = host.split(".")
  if (labels.length < 2) return null // single-label hosts, "localhost"
  if (!labels.every((l) => LABEL.test(l))) return null // empty labels, a trailing dot, "_"
  if (!TLD.test(labels[labels.length - 1] ?? "")) return null // IP literals
  if (BLOCKED_SUFFIXES.some((s) => host.endsWith(s))) return null
  return host
}

/**
 * A link a document may carry: `https:` only, ≤ 2,000 characters. Refused: userinfo, an explicit port, IP literals,
 * `localhost`, single-label hosts, hosts ending in .local, .internal, .lan, .localhost, .test, whitespace and "\\".
 * The cloud never fetches it (an invariant of decision 0068).
 */
export const isDocUrl = (url: string): boolean => docUrlHost(url) !== null

export const DocUrl = Schema.String.check(
  Schema.makeFilter((s: string) =>
    isDocUrl(s)
      ? true
      : "an https: link (no userinfo, port, IP address, localhost or internal host), ≤ 2,000 characters",
  ),
)

/** `doc` on an artifact: where it sits and the live original. At least one of the two is set. */
export const DocMeta = Schema.Struct({
  /** Its place; also the import key. Null only for a link added without a path. */
  path: Schema.NullOr(DocPath),
  /** The live original: a claude.ai artifact, a Google doc, a GitHub blob URL. */
  url: Schema.NullOr(DocUrl),
  /** Where edits are authoritative. Older repo imports keep their external home. */
  home: Schema.optionalKey(Schema.Literals(["minga", "external"])),
  /** The revision actually imported from the external source (for example a Git commit). */
  sourceRevision: Schema.optionalKey(Schema.NonEmptyString.check(Schema.isMaxLength(200))),
  /** A visible Markdown notebook for a persistent project agent; it remains an ordinary versioned document. */
  notebookFor: Schema.optionalKey(AgentId),
}).check(
  Schema.makeFilter((m) => m.path !== null || m.url !== null || "a document has a path or a url"),
  Schema.makeFilter(
    (m) => m.home !== "external" || m.url !== null || "an external home needs its source url",
  ),
)
export type DocMeta = typeof DocMeta.Type

/**
 * Why a document is in a run's context. Decision 0084 added `project_page` (pinned by path, `pages.ts`) and `guideline`
 * (`guidelines/*`, §3.3).
 *
 * Forward compatible (incident 2026-10-15: runner 0.9.4 could not decode a `turn.start` carrying the 0084 reasons and
 * its threads stalled at "Preparing workspace"): any other string decodes as `linked_document`, the generic reason.
 * Runners only carry the list (nothing branches on the reason), so a newer cloud's reason never fails an older runner's
 * decode. Encoding stays strict: the cloud only writes the reasons above.
 */
export const LOADED_DOCUMENT_REASONS = ["notebook", "linked_document", "project_page", "guideline"] as const
export const LoadedDocumentReason = tolerantLiterals(LOADED_DOCUMENT_REASONS, "linked_document")
export type LoadedDocumentReason = typeof LoadedDocumentReason.Type

/**
 * Who wrote a loaded document revision. Forward compatible like {@link LoadedDocumentReason}: an actor this protocol
 * does not know (a newer `system` process, decision 0086's `people` for runners before it) decodes as an opaque
 * `{_tag: "unknown", tag, process?}` instead of failing the whole `turn.start`. Display code names it "Minga".
 */
export const UnknownDocumentActor = Schema.Struct({
  _tag: Schema.String,
  process: Schema.optionalKey(Schema.String),
}).pipe(
  Schema.decodeTo(
    Schema.toType(
      Schema.Struct({
        _tag: Schema.Literal("unknown"),
        tag: Schema.String,
        process: Schema.optionalKey(Schema.String),
      }),
    ),
    {
      decode: SchemaGetter.transform((a) => ({
        _tag: "unknown" as const,
        tag: a._tag,
        ...(a.process !== undefined ? { process: a.process } : {}),
      })),
      encode: SchemaGetter.transform((a) => ({
        _tag: a.tag,
        ...(a.process !== undefined ? { process: a.process } : {}),
      })),
    },
  ),
)
export const LoadedDocumentActor = Schema.Union([Actor, UnknownDocumentActor])
export type LoadedDocumentActor = typeof LoadedDocumentActor.Type

/** The exact document revision included in a run's content-addressed context. */
export const LoadedDocument = Schema.Struct({
  artifactId: ArtifactId,
  ref: Schema.NullOr(ShortRef),
  path: Schema.NullOr(DocPath),
  revision: PosInt,
  sha256: Sha256,
  by: LoadedDocumentActor,
  reason: LoadedDocumentReason,
  tainted: Schema.Boolean,
  sourceRevision: Schema.optionalKey(Schema.String),
})
export type LoadedDocument = typeof LoadedDocument.Type

export const docHomeOf = (doc: DocMeta): "minga" | "external" =>
  doc.home ?? (doc.url !== null && repoSourceOf(doc.url) !== null ? "external" : "minga")

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
const TAIL = "/?(?:[?#][^\\s]*)?$"
const CLAUDE_SHAPES: ReadonlyArray<{ readonly re: RegExp; readonly published: boolean }> = [
  { re: new RegExp(`^https://claude\\.ai/artifact/([A-Za-z0-9_-]{1,64})${TAIL}`), published: false },
  { re: new RegExp(`^https://claude\\.ai/code/artifact/(${UUID})${TAIL}`), published: false },
  { re: new RegExp(`^https://claude\\.ai/public/artifacts/(${UUID})${TAIL}`), published: true },
  { re: new RegExp(`^https://claude\\.site/artifacts/(${UUID})${TAIL}`), published: true },
]

/**
 * A Claude artifact link (research §3.1), matched on the exact lowercase host with no port or userinfo; never fetched.
 * `https://claude.ai/artifact/<id>` and `https://claude.ai/code/artifact/<uuid>` are private (`published: false`);
 * `https://claude.ai/public/artifacts/<uuid>` and `https://claude.site/artifacts/<uuid>` are published.
 */
export const claudeArtifactOf = (
  url: string,
): { readonly published: boolean; readonly id: string } | null => {
  if (!isDocUrl(url)) return null
  for (const { re, published } of CLAUDE_SHAPES) {
    const id = re.exec(url)?.[1]
    if (id !== undefined) return { published, id }
  }
  return null
}

const GITHUB_BLOB =
  /^https:\/\/github\.com\/([A-Za-z0-9-]{1,39})\/([A-Za-z0-9._-]{1,100})\/blob\/([^/?#\s]+)\/([^?#\s]+)/

/**
 * A GitHub blob URL (`https://github.com/<owner>/<repo>/blob/<ref>/<path>`): the repo is the document's home, so
 * agents may not post versions of it (spec §3 rule 7). `repo` is `owner/repo`; `path` is percent-decoded.
 */
export const repoSourceOf = (url: string): { readonly repo: string; readonly path: string } | null => {
  if (!isDocUrl(url)) return null
  const m = GITHUB_BLOB.exec(url)
  if (!m || m[1] === undefined || m[2] === undefined || m[4] === undefined) return null
  if (m[2] === "." || m[2] === "..") return null
  let path: string
  try {
    path = decodeURIComponent(m[4])
  } catch {
    return null
  }
  return { repo: `${m[1]}/${m[2]}`, path }
}
// #endregion paths and links

// #region kinds
/** What people see. `support`: JS, CSS and fonts (hidden in lists; they feed HTML views). `file`: anything else. */
export const DocKind = Schema.Literals([
  "markdown",
  "html",
  "image",
  "pdf",
  "text",
  "link",
  "support",
  "file",
])
export type DocKind = typeof DocKind.Type

/** Support files: never executed on the app origin, served with their real type only by the user-content Worker. */
export const SUPPORT_TYPES: ReadonlySet<FileContentType> = new Set<FileContentType>([
  "text/javascript",
  "text/css",
  "font/woff2",
  "font/woff",
  "font/ttf",
  "font/otf",
])

/** The kind of a document from its URI and content type (a non-file URI is a link). */
export const docKindOf = (a: { readonly uri: string; readonly contentType: string | null }): DocKind => {
  if (shaOfFileUri(a.uri) === null) return "link"
  const ct = a.contentType === null ? null : fileContentTypeOf(a.contentType)
  if (ct === null) return "file"
  if (SUPPORT_TYPES.has(ct)) return "support"
  if (ct === "text/markdown") return "markdown"
  if (ct === "text/html") return "html"
  if (ct === "application/pdf") return "pdf"
  if (ct.startsWith("image/")) return "image"
  if (ct === "text/plain" || ct === "text/csv" || ct === "application/json") return "text"
  return "file"
}
// #endregion kinds

// #region limits
/** Spec §10. */
export const DOC_LIMITS = {
  /** Rows per `post_document` call. */
  rows: 50,
  /** Inline `content` per row, UTF-8 bytes. */
  inlineBytes: 262_144,
  /** Inline `content` across one call (under the MCP's 1 MB body cap). */
  inlineBytesPerCall: 900_000,
  /** `list_documents` rows: maximum and default. */
  listMax: 500,
  listDefault: 200,
  /** `list_documents` answer size before it stops and sets `more`. */
  listAnswerBytes: 1_000_000,
  title: 200,
  notes: 2_000,
  path: DOC_PATH_MAX,
  url: DOC_URL_MAX,
} as const

/** UTF-8 length without TextEncoder (not in the ES2023 lib); a lone surrogate counts 3 bytes, like U+FFFD. */
export const utf8Length = (s: string): number => {
  let n = 0
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    if (c < 0x80) n += 1
    else if (c < 0x800) n += 2
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1)
      if (d >= 0xdc00 && d <= 0xdfff) {
        n += 4
        i++
      } else n += 3
    } else n += 3
  }
  return n
}
// #endregion limits

// #region tool inputs
/** A project id, slug or name (the person MCP's `project`). */
const ProjectRef = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64))
const Title = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(DOC_LIMITS.title))

/** The type of a file row's bytes: from the path's extension, else from `kind` (markdown, html, text). */
export const contentTypeOfDocRow = (row: {
  readonly path?: string | undefined
  readonly kind?: "markdown" | "html" | "text" | "link" | undefined
}): FileContentType | null => {
  if (row.path !== undefined) {
    const name = row.path.slice(row.path.lastIndexOf("/") + 1)
    const dot = name.lastIndexOf(".")
    const byExt = dot > 0 ? contentTypeOfExt(name.slice(dot + 1)) : null
    if (byExt !== null) return byExt
  }
  switch (row.kind) {
    case "markdown":
      return "text/markdown"
    case "html":
      return "text/html"
    case "text":
      return "text/plain"
    default:
      return null
  }
}

const PostDocumentRowFields = Schema.Struct({
  /** Its place (`docs/design/x/README.md`); also the import key. */
  path: Schema.optionalKey(DocPath),
  /** A link document, or the live source of a file (claude.ai, GitHub). */
  url: Schema.optionalKey(DocUrl),
  /** Text bytes inline (≤ 262,144 UTF-8 bytes). */
  content: Schema.optionalKey(Schema.String),
  /** Bytes uploaded through the answer's `upload.base`; the type comes from the path's extension or `kind`. */
  file: Schema.optionalKey(
    Schema.Struct({
      sha256: Sha256,
      sizeBytes: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: FILE_MAX_BYTES })),
    }),
  ),
  /** When the path has no extension. */
  kind: Schema.optionalKey(Schema.Literals(["markdown", "html", "text", "link"])),
  /** Default: the head's title, else the file name, else "Claude artifact" / the URL's host. */
  title: Schema.optionalKey(Title),
  /** Stored in `preview`; searched. */
  notes: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(DOC_LIMITS.notes))),
  /** `O-n` or artifact id; default: the live document with this path, else (no path) the link with this url. */
  replaces: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64))),
  /** Text from outside the team (decision 0050 §10). */
  untrusted: Schema.optionalKey(Schema.Boolean),
  /** With `path` or `replaces` only: Remove that document. */
  remove: Schema.optionalKey(Schema.Literal(true)),
})
type PostDocumentRowShape = typeof PostDocumentRowFields.Type

/** Why a row is not well formed, else undefined (spec §4.2: exactly one of content, file, url alone, or remove). */
export const postDocumentRowIssue = (r: PostDocumentRowShape): string | undefined => {
  const bytes = (r.content !== undefined ? 1 : 0) + (r.file !== undefined ? 1 : 0)
  if (r.remove === true) {
    if (bytes > 0 || r.url !== undefined) return "a remove row has only path or replaces"
    if (r.path === undefined && r.replaces === undefined) return "a remove row needs path or replaces"
    return undefined
  }
  if (bytes > 1) return "send content or file, not both"
  if (bytes === 0) {
    if (r.url === undefined) return "a row has one of content, file, url (a link) or remove"
    if (r.kind !== undefined && r.kind !== "link") return "a link row has kind link (or none)"
    return undefined
  }
  if (r.kind === "link") return "kind link is for a row with url alone"
  if (contentTypeOfDocRow(r) === null)
    return "the type comes from the path's extension (.md, .html, .png, …) or kind (markdown, html, text)"
  if (r.content !== undefined && utf8Length(r.content) > DOC_LIMITS.inlineBytes)
    return `inline content is at most ${DOC_LIMITS.inlineBytes} bytes: upload it as file`
  return undefined
}

/** One row of `post_document`. */
export const PostDocumentRow = PostDocumentRowFields.check(
  Schema.makeFilter((r: PostDocumentRowShape) => postDocumentRowIssue(r) ?? true),
)
export type PostDocumentRow = typeof PostDocumentRow.Type

/**
 * The person MCP's document tools (spec §4.2). Not part of `PersonToolInputs` (`ProjectDO.personTool` does not run
 * them): `apps/cloud/src/mcp/documents.ts` lists and runs them, like wave K's `KnowledgePersonToolInputs`.
 */
export const DocumentPersonToolInputs = {
  post_document: Schema.Struct({
    project: ProjectRef,
    documents: Schema.Array(PostDocumentRow).check(
      Schema.isMinLength(1),
      Schema.isMaxLength(DOC_LIMITS.rows),
    ),
  }).check(
    Schema.makeFilter((call) => {
      let inline = 0
      for (const r of call.documents) if (r.content !== undefined) inline += utf8Length(r.content)
      return (
        inline <= DOC_LIMITS.inlineBytesPerCall ||
        `inline content across one call is at most ${DOC_LIMITS.inlineBytesPerCall} bytes: upload the rest as file`
      )
    }),
  ),
  list_documents: Schema.Struct({
    project: ProjectRef,
    /** A path prefix. */
    under: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(DOC_PATH_MAX))),
    /** Title or path contains (case-insensitive). */
    query: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(200))),
    /** The `more` cursor of the previous answer. */
    after: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(400))),
    /** Default 200. */
    limit: Schema.optionalKey(
      Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: DOC_LIMITS.listMax })),
    ),
  }),
} as const
export type DocumentToolName = keyof typeof DocumentPersonToolInputs
export const DocumentToolNames = Object.keys(DocumentPersonToolInputs) as ReadonlyArray<DocumentToolName>
export const isDocumentToolName = (name: string): name is DocumentToolName =>
  Object.hasOwn(DocumentPersonToolInputs, name)

/**
 * Title and annotations (the O1 shape of `PERSON_TOOL_META`). `post_document` is destructive (an update changes what
 * `O-n`, search and Lea's `read` show for the whole team) and not idempotent (retries replay by the call's seed).
 */
export const DOCUMENT_TOOL_META: Readonly<Record<DocumentToolName, PersonToolMeta>> = {
  post_document: {
    title: "Add or update project documents",
    annotations: {
      title: "Add or update project documents",
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  list_documents: {
    title: "List project documents",
    annotations: { title: "List project documents", readOnlyHint: true, openWorldHint: false },
  },
}

/** The tools' descriptions (person voice, spec §4.2). */
export const DOCUMENT_TOOL_DESCRIPTIONS: Readonly<Record<DocumentToolName, string>> = {
  post_document:
    "Add, update or remove documents in a Minga project: Markdown, HTML (with its scripts, styles, fonts and images as separate files), images, PDFs, or a link (a Claude artifact, a Google doc). Give each a `path` (where it sits, e.g. `docs/design/x/README.md`). Posting the same path again makes a new version with the same ref; unchanged files are skipped. Send short text as `content`. For other files send `file: {sha256, sizeBytes}`; the answer's `upload.base` takes `PUT <base>/<sha256>.<ext>` for each missing file; then call again. Up to 50 rows a call. Read the `import-docs` guide for a whole folder.",
  list_documents:
    "List a project's documents (newest versions): ref, title, kind, path, source link, sha256 and who updated it when. Use it before an import to skip files that did not change and to find files that left the folder.",
}
// #endregion tool inputs

// #region answers
export const PostDocumentStatus = Schema.Literals([
  "created",
  "updated",
  "unchanged",
  "removed",
  "needs_upload",
  "error",
])
export type PostDocumentStatus = typeof PostDocumentStatus.Type

export const PostDocumentRowResult = Schema.Struct({
  /** The row's index in the call. */
  index: NonNegInt,
  path: Schema.NullOr(Schema.String),
  status: PostDocumentStatus,
  /** The document's `O-n` (null for needs_upload of a new path, and for errors before a target is known). */
  ref: Schema.NullOr(ShortRef),
  /** The absolute app link of the document's page (`<origin>/w/<ws>/p/<project>/o/O-14`). */
  page: Schema.NullOr(Schema.String),
  error: Schema.optionalKey(Schema.String),
})
export type PostDocumentRowResult = typeof PostDocumentRowResult.Type

/** `PUT <base>/<sha256>.<ext>` for each missing file, then call again with the same rows. */
export const DocUploadBlock = Schema.Struct({
  base: Schema.String,
  missing: Schema.Array(Sha256),
  expiresAt: Millis,
})
export type DocUploadBlock = typeof DocUploadBlock.Type

export const PostDocumentResult = Schema.Struct({
  documents: Schema.Array(PostDocumentRowResult),
  /** Once per call, covering every missing sha of the call. */
  upload: Schema.optionalKey(DocUploadBlock),
  counts: Schema.Struct({
    created: NonNegInt,
    updated: NonNegInt,
    unchanged: NonNegInt,
    removed: NonNegInt,
    needs_upload: NonNegInt,
    error: NonNegInt,
  }),
})
export type PostDocumentResult = typeof PostDocumentResult.Type

export const ListDocumentsRow = Schema.Struct({
  ref: ShortRef,
  title: Schema.String,
  kind: DocKind,
  path: Schema.NullOr(Schema.String),
  url: Schema.NullOr(Schema.String),
  /** The url is a Claude artifact link. */
  claude: Schema.Boolean,
  sha256: Schema.NullOr(Sha256),
  /** Versions in the chain (1 = never updated). */
  versions: PosInt,
  updatedAt: Millis,
  /** A display name ("Jannik", "Lea"). */
  updatedBy: Schema.String,
  byAgent: Schema.Boolean,
  tainted: Schema.Boolean,
  page: Schema.String,
})
export type ListDocumentsRow = typeof ListDocumentsRow.Type

export const ListDocumentsResult = Schema.Struct({
  documents: Schema.Array(ListDocumentsRow),
  /** Pass as `after` for the next page. */
  more: Schema.optionalKey(Schema.String),
})
export type ListDocumentsResult = typeof ListDocumentsResult.Type
// #endregion answers

// #region http
/** F7 `POST /api/workspaces/:ws/projects/:id/files/view`: the caller names a version, never files. */
export const DocViewBody = Schema.Struct({ artifactId: ArtifactId })
export type DocViewBody = typeof DocViewBody.Type

/**
 * F7's answer. `scripts` and `reason` come from core `viewOf`; `reason` says why scripts are off ("Lea (agent) wrote
 * this version"), null when they run. A purged or unknown version is an error answer (`{error: "purged"}`, 410).
 */
export const DocViewResult = Schema.Struct({
  url: Schema.String,
  expiresAt: Millis,
  scripts: Schema.Boolean,
  reason: Schema.NullOr(Schema.String),
})
export type DocViewResult = typeof DocViewResult.Type

/** Purge `POST /api/workspaces/:ws/projects/:id/files/purge` (spec §4.5). */
export const DocPurgeBody = Schema.Struct({ sha256: Sha256 })
export type DocPurgeBody = typeof DocPurgeBody.Type

export const DocPurgeResult = Schema.Struct({
  /** Live documents removed (set `rejected`). */
  removed: NonNegInt,
  /** The R2 object was deleted (no other project had the sha). */
  purgedBytes: Schema.Boolean,
})
export type DocPurgeResult = typeof DocPurgeResult.Type
// #endregion http
